import { Badge } from '@shared/components/ui/badge';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { Product } from '@shared/components/ui/Product';
import { UserCard } from '@shared/components/ui/usercard';
import { usePermissions } from '@shared/hooks/usePermissions';
import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getMovementBatchDisplayLabel, MovementBatchCell } from '@modules/Reports/components/MovementBatchCell';
import { renderMovementOrder, renderMovementTask } from '../components/display/renderShopFromEvent';
import { StockMovementsModel } from '../models/StockMovements';

const getDisplayQuantity = (data: StockMovementsModel | null | undefined): number => {
  if (!data) return 0;
  const qty = data.qty ?? 0;
  if (data.from_type_id === 1 && data.to_type_id !== 1) {
    return -qty;
  }
  return qty;
};

const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const useAgGridColumns = () => {
  const { t } = useTranslation('stockmovementsModule');
  const { getTeamType } = usePermissions();
  const isTenant3pl = getTeamType() === 'tenant_3pl';

  const allColumnDefs = useMemo<ColDef<StockMovementsModel>[]>(() => {
    const baseColumns: ColDef<StockMovementsModel>[] = [
      {
        colId: 'product',
        field: 'product.name',
        headerName: t('table.product'),
        sortable: false,
        minWidth: 250,
        cellRenderer: ({ data }: { data: StockMovementsModel }) =>
          data?.product?.name ? (
            <Product
              src={data.product.image || ''}
              name={data.product.name}
              code={data.product.code || ''}
              id={data.product.id}
              enableLineClamp={false}
              showTooltip={true}
              showImageModal={true}
            />
          ) : null,
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'batch',
        field: 'batch.batch_number',
        headerName: t('table.batch'),
        sortable: false,
        minWidth: 280,
        suppressSizeToFit: true,
        cellStyle: { display: 'flex', alignItems: 'center' },
        cellRenderer: ({ data }: { data: StockMovementsModel }) =>
          data ? <MovementBatchCell item={data} i18nNs="stockmovementsModule" /> : null,
        valueGetter: ({ data }) => (data ? getMovementBatchDisplayLabel(data) : ''),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'qty',
        field: 'qty',
        headerName: t('table.amount'),
        sortable: true,
        type: 'numericColumn',
        minWidth: 120,
        maxWidth: 150,
        cellStyle: { textAlign: 'left' },
        valueGetter: ({ data }) => getDisplayQuantity(data),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'available_stock_after_event',
        field: 'available_stock_after_event',
        headerComponent: () => (
          <div className="flex items-center gap-2">
            <span className="text-title">{t('table.total')}</span>
            <InfoTooltip text={t('tooltip.total_description')} dataTestId="total-tooltip" position="bottom" />
          </div>
        ),
        headerTooltip: t('tooltip.total_description'),
        sortable: false,
        minWidth: 140,
        maxWidth: 180,
        cellRenderer: ({ value }: { value: number | null }) => (
          <Badge variant="outline" className="text-text">
            {value ?? 0}
          </Badge>
        ),
        valueGetter: ({ data }) => data?.available_stock_after_event ?? 0,
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'event',
        field: 'event.name',
        headerName: t('table.type'),
        sortable: false,
        minWidth: 160,
        cellRenderer: ({ data }: { data: StockMovementsModel }) => {
          const eventKey = data?.event?.name ?? String(data?.event?.id ?? '');
          const eventName = t(`filters.options.${eventKey}`, {
            defaultValue: eventKey || '-',
          }).toString();

          return <span className="text-sm font-medium">{eventName}</span>;
        },
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'order',
        field: 'order.ref',
        headerName: t('table.order'),
        sortable: false,
        minWidth: 150,
        maxWidth: 200,
        cellStyle: { display: 'flex', alignItems: 'center' },
        cellRenderer: ({ data }: { data: StockMovementsModel }) => (data ? renderMovementOrder(data) : null),
        suppressHeaderMenuButton: true,
      },
    ];

    if (isTenant3pl) {
      baseColumns.push({
        colId: 'task',
        field: 'stock_location_order_id',
        headerName: t('table.task'),
        sortable: false,
        minWidth: 160,
        maxWidth: 220,
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-start' },
        cellRenderer: ({ data }: { data: StockMovementsModel }) => (data ? renderMovementTask(data) : null),
        suppressHeaderMenuButton: true,
      });
    }

    baseColumns.push({
      colId: 'description',
      field: 'description',
      headerName: t('table.description'),
      sortable: false,
      minWidth: 160,
      flex: 1,
      cellRenderer: ({ data }: { data: StockMovementsModel }) => {
        const text = data?.description?.trim();
        return <span className="text-xs text-title line-clamp-2 flex items-center h-full">{text || '-'}</span>;
      },
      valueGetter: ({ data }) => data?.description ?? '',
      suppressHeaderMenuButton: true,
    });

    if (isTenant3pl) {
      baseColumns.push({
        colId: 'user',
        field: 'user.name',
        headerName: t('table.user'),
        sortable: false,
        minWidth: 200,
        cellRenderer: ({ data }: { data: StockMovementsModel }) => {
          const name = data?.user?.name;
          const image = data?.user?.image;

          if (!name) {
            return <span className="text-sm text-complementary-text">{t('fallbacks.no_user')}</span>;
          }

          return (
            <UserCard name={name} email={data?.user?.email ?? ''} data-testid={`user-${data?.id}`} avatarUrl={image} />
          );
        },
        suppressHeaderMenuButton: true,
      });
    }

    baseColumns.push({
      colId: 'created_at',
      field: 'created_at',
      headerName: t('table.date'),
      sortable: true,
      minWidth: 160,
      maxWidth: 200,
      cellRenderer: ({ data }: { data: StockMovementsModel }) => {
        const formatted = formatDateTime(data?.created_at);
        return <span className="text-sm font-medium">{formatted}</span>;
      },
      valueGetter: ({ data }) => data?.created_at ?? '',
      suppressHeaderMenuButton: true,
    });

    if (isTenant3pl) {
      baseColumns.push({
        colId: 'team',
        field: 'team',
        headerName: t('table.client'),
        sortable: false,
        minWidth: 200,
        cellRenderer: ({ data }: { data: StockMovementsModel }) => {
          return data?.team?.name ? (
            <UserCard name={data.team.name} email="" />
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          );
        },
        valueGetter: ({ data }) => data?.team?.name ?? '',
        suppressHeaderMenuButton: true,
      });
    }

    return baseColumns;
  }, [t, isTenant3pl]);

  return { allColumnDefs };
};
